/**
 * ApiAgentRepository - Implémentation API du AgentRepository
 * Clean Architecture - Infrastructure Layer
 * 
 * Communique avec le backend RASK via REST API
 */

import { AgentRepository } from '../../../domain/agent/repositories/AgentRepository';
import { BACKEND_URL } from '../../../core/config/environment.config';

const pruneUndefined = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value;

  return Object.entries(value).reduce((acc, [key, item]) => {
    if (item === undefined) return acc;
    const cleaned = pruneUndefined(item);
    if (cleaned && typeof cleaned === 'object' && !Array.isArray(cleaned)) {
      if (Object.keys(cleaned).length === 0) return acc;
      acc[key] = cleaned;
      return acc;
    }
    acc[key] = cleaned;
    return acc;
  }, {});
};

const normalizeContext = (context) => {
  if (!context) return undefined;
  const cleaned = pruneUndefined(context);
  if (!cleaned || Object.keys(cleaned).length === 0) return undefined;
  return cleaned;
};

/**
 * Erreurs spécifiques à l'agent
 */
export class AgentApiError extends Error {
  constructor(message, statusCode, originalError = null, errorCode = null) {
    super(message);
    this.name = 'AgentApiError';
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.errorCode = errorCode; // Code d'erreur pour i18n
  }
}

/**
 * Implémentation API du repository Agent
 */
export class ApiAgentRepository extends AgentRepository {
  /**
   * @param {string} baseUrl - URL de base du backend
   * @param {number} [timeout=30000] - Timeout en ms
   */
  constructor(baseUrl, timeout = 30000) {
    super();
    this.baseUrl = baseUrl || BACKEND_URL;
    this.timeout = timeout;
  }

  /**
   * Envoie un message à l'agent RASK
   * @param {Object} request - Requête à envoyer
   * @param {string} authToken - Token Firebase
   * @returns {Promise<Object>}
   */
  async sendMessage(request, authToken) {
    if (!authToken) {
      throw new AgentApiError('Authentication token required', 401, null, 'AUTH_TOKEN_REQUIRED');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const context = normalizeContext(request.context);
      const requestBody = {
        message: request.message,
        conversationId: request.conversationId, // UUID de la conversation pour mémorisation
        ...(context ? { context } : {}),
      };

      const response = await fetch(`${this.baseUrl}/agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        await this.handleErrorResponse(response);
      }

      const data = await response.json();
      
      return {
        success: true,
        message: data.message || '',
        actionCards: data.actionCards || [],
        conversationId: data.conversationId,
        metadata: data.metadata,
        uiResult: data.uiResult,
      };

    } catch (error) {
      clearTimeout(timeoutId);

      if (error.name === 'AbortError') {
        throw new AgentApiError(
          'Timeout',
          504,
          null,
          'TIMEOUT'
        );
      }

      if (error instanceof AgentApiError) {
        throw error;
      }

      if (!navigator.onLine) {
        throw new AgentApiError(
          'Network error',
          0,
          null,
          'NETWORK_ERROR'
        );
      }

      throw new AgentApiError(
        'Unknown error',
        500,
        error,
        'UNKNOWN_ERROR'
      );
    }
  }

  /**
   * Gère les réponses d'erreur HTTP
   * @param {Response} response - Réponse fetch
   */
  async handleErrorResponse(response) {
    let errorCode;
    let errorMessage;

    switch (response.status) {
      case 401:
        errorCode = 'SESSION_EXPIRED';
        errorMessage = 'Session expired';
        break;
      case 403:
        errorCode = 'UNAUTHORIZED_ACCESS';
        errorMessage = 'Unauthorized access';
        break;
      case 429:
        errorCode = 'TOO_MANY_REQUESTS';
        errorMessage = 'Too many requests';
        break;
      case 500:
      case 502:
      case 503:
        errorCode = 'SERVER_ERROR';
        errorMessage = 'Server error';
        break;
      case 504:
        errorCode = 'TIMEOUT';
        errorMessage = 'Timeout';
        break;
      default:
        errorCode = 'UNKNOWN_ERROR';
        errorMessage = 'Unknown error';
    }

    throw new AgentApiError(errorMessage, response.status, null, errorCode);
  }

  /**
   * Vérifie la santé du service agent
   * @returns {Promise<Object>}
   */
  async checkHealth() {
    try {
      const response = await fetch(`${this.baseUrl}/agent/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        return { healthy: false, timestamp: new Date().toISOString() };
      }

      const data = await response.json();
      return {
        healthy: data.status === 'healthy',
        timestamp: data.timestamp,
        metrics: data.metrics,
      };
    } catch (error) {
      return { healthy: false, timestamp: new Date().toISOString() };
    }
  }

  /**
   * Récupère les métriques du service
   * @param {string} authToken - Token Firebase
   * @returns {Promise<Object>}
   */
  async getMetrics(authToken) {
    if (!authToken) {
      throw new AgentApiError('Authentication token required', 401, null, 'AUTH_TOKEN_REQUIRED');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(`${this.baseUrl}/agent/metrics`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new AgentApiError('Metrics fetch failed', response.status, null, 'METRICS_FETCH_FAILED');
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new AgentApiError('Timeout', 504, error, 'TIMEOUT');
      }
      if (error instanceof AgentApiError) {
        throw error;
      }
      throw new AgentApiError('Metrics fetch error', 500, error, 'METRICS_ERROR');
    }
  }
}

export default ApiAgentRepository;
